import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroupDirective, NgForm, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IconNames } from '../../../common/constants/icon-names';
import { AutoUnsubscribe } from '../../../common';
import { ErrorStateMatcher } from '@angular/material/core';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-address-select',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,
    SvgIconComponent,
  ],
  templateUrl: './address-select.component.html',
  styleUrls: ['./address-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class AddressSelectComponent<T extends { fullAddress: string }> implements OnInit {
  @Input() addresses: T[];
  @Input() fieldLabel: string;
  @Input() disabled = false;
  @Input() testId: string;
  @Input() searchAddressControl: FormControl<string>;
  @Input() isLoadMoreEnabled: boolean;
  @Input() loadMoreAmount = 10;
  @Input() fieldControl: FormControl<T>;

  @ViewChild('addressAuto') addressAuto: MatAutocomplete;

  @Output() loadMore: EventEmitter<{ more: number; optionAmount: number }> = new EventEmitter(null);
  @Output() optionSelected: EventEmitter<T> = new EventEmitter(null);

  iconNames = IconNames;
  subscriptions: Subscription[] = [];

  errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (_: FormControl | null, form: FormGroupDirective | NgForm | null): boolean => {
      const c = this.fieldControl;
      const submitted = !!form && form.submitted;
      return !!c && c.invalid && (c.touched || c.dirty || submitted);
    },
  };

  onBlur(): void {
    this.fieldControl?.markAsTouched();
    this.fieldControl?.updateValueAndValidity({ emitEvent: false });

    this.cd.markForCheck();
  }

  constructor(private cd: ChangeDetectorRef) {}

  onLoadMore(): void {
    const optionAmount = this.addressAuto.options.length;

    this.loadMore.emit({ more: this.loadMoreAmount, optionAmount });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.optionSelected.emit(event.option.value);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.fieldControl.statusChanges.subscribe(() => this.cd.markForCheck()),
      this.fieldControl.valueChanges.subscribe(() => this.cd.markForCheck()),
    );

    if (this.searchAddressControl) {
      this.subscriptions.push(
        this.searchAddressControl.statusChanges.subscribe(() => this.cd.markForCheck()),
        this.searchAddressControl.valueChanges.subscribe(() => this.cd.markForCheck()),
      );
    }
  }
}
